<template>
  <div class="button-wrapper">
    <button
      :class="[
        {
          active: active,
          'cue-pad--set': setMode,
          'cue-pad--clear': clearMode
        },
        noteNrClass
      ]"
      class="cue-pad ripple"
      @mousedown.ctrl="setCuePoint()"
      @mousedown.exact="seekYT()"
    >
      {{ note }}
      <transition name="fade">
        <span
          v-show="nudgeMode"
          :class="{ 'cue-pad__nudge--visible': nudgeMode }"
          class="cue-pad__nudge cue-pad__nudge--left"
        >
          <svgicon
            @click="decrementCuePoint()"
            icon="chevrons-left"
            width="22"
            height="18"
            color="#fff"
          ></svgicon>
        </span>
      </transition>
      <transition name="fade">
        <span
          v-show="nudgeMode"
          :class="{ 'cue-pad__nudge--visible': nudgeMode }"
          class="cue-pad__nudge cue-pad__nudge--right"
        >
          <svgicon
            @click="incrementCuePoint()"
            icon="chevrons-right"
            width="22"
            height="18"
            color="#fff"
          ></svgicon>
        </span>
      </transition>
      <span
        v-if="nudgeMode"
        class="cue-pad__cue-point-value cue-pad__cue-point-value--nudge"
      >{{ thisAccurateCuePoint }}</span>
      <span v-else class="cue-pad__cue-point-value">{{ thisCuePoint }}</span>
    </button>
  </div>
</template>

<script>
import EventBus from "../eventBus";
import "@/compiled-icons/chevrons-left";
import "@/compiled-icons/chevrons-right";

export default {
  name: "CuePad",
  props: {
    note: {
      type: String,
      required: true
    },
    noteNr: {
      type: Number
    }
  },
  data() {
    return {
      active: false
    };
  },
  methods: {
    activate() {
      this.active = true;
      // remove active state after timeout
      setTimeout(() => {
        this.active = false;
      }, 300);
    },
    setCuePoint() {
      this.$store.commit("setNote", this.note); // Set current note in store
      EventBus.$emit("SET_CUEPOINT", this.note); // Pass cue point event over to YouTubePlayer component
    },
    incrementCuePoint() {
      this.$store
        .dispatch("incrementCuePoint", this.note)
        .then(this.$store.dispatch("playNote", this.note));
    },
    decrementCuePoint() {
      this.$store
        .dispatch("decrementCuePoint", this.note)
        .then(this.$store.dispatch("playNote", this.note));
    },
    seekYT() {
      if (this.setMode) {
        this.setCuePoint();
      } else if (this.clearMode) {
        this.$store.commit("clearCuePoint", this.note);
      } else {
        this.$store.commit("setNote", this.note); // Set current note in store
        EventBus.$emit("YT_GOTO", this.note); // Emit timeline event to YouTubePlayer component
      }
    }
  },
  computed: {
    setMode() {
      return this.$store.state.config.setMode;
    },
    clearMode() {
      return this.$store.state.config.clearMode;
    },
    nudgeMode() {
      return this.$store.state.config.nudgeMode;
    },
    noteNrClass() {
      return "cue-pad--" + this.noteNr; // Add pad number for animation purposes
    },
    storeNote() {
      return this.$store.state.volatile.currentNote;
    },
    thisCuePoint() {
      return this.$store.getters.prettyCuePoint(this.note);
    },
    thisAccurateCuePoint() {
      return this.$store.getters.accurateCuePoint(this.note);
    }
  },
  watch: {
    storeNote(newNote) {
      // Watch change note and trigger active state
      if (this.note == newNote) {
        this.activate;
      }
    }
  },
  mounted() {
    // Listen for MIDI triggers
    EventBus.$on("YT_TRIG", note => {
      if (this.note === note) {
        this.seekYT();
        this.active = true;
        setTimeout(() => {
          this.active = false;
        }, 200);
      }
    });
  }
};
</script>

<style lang="scss">
@import "../partials/vars";
.button-wrapper {
  width: 30%;
  @media (min-width: $break-small) {
    width: 23%;
  }
}
.cue-pad {
  @include btn;
  font-size: 20px;
  margin: 6px 1%;
  height: 100px;
  display: block;
  width: 100%;
  position: relative;

  &--set {
    animation: colorPulseSet 0.4s infinite alternate;
  }
  &--clear {
    animation: colorPulseClear 0.4s infinite alternate;
  }

  @for $i from 1 through 12 {
    &--#{$i} {
      animation-delay: #{$i/10}s;
    }
  }

  @keyframes colorPulseSet {
    0% {
      background-color: $color-gray-l;
    }
    100% {
      background-color: $color-gray-green;
    }
  }

  @keyframes colorPulseClear {
    0% {
      background-color: $color-gray-red;
    }
    100% {
      background-color: $color-gray-l;
    }
  }

  &.active,
  &:hover {
    background-color: $color-base;
  }

  &:focus {
    outline: none;
  }

  &__cue-point-value {
    display: block;
    position: absolute;
    font-weight: normal;
    font-style: italic;
    font-size: 12px;
    left: 0;
    bottom: 0;
    background-color: $color-gray;
    padding: 5px;
    min-width: 60px;
    transition: min-width 0.2s ease-in;

    &--nudge {
      font-weight: 700;
      min-width: 100%;
    }
  }

  &__nudge {
    position: absolute;
    bottom: 0;
    z-index: 1;
    height: 24px;
    padding: 3px 9px;
    transition: all 0.1s ease-in;

    &:hover {
      background-color: $color-gray-dd;
    }

    &--left {
      left: 0;
    }
    &--right {
      right: 0;
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
  transition-delay: 0.2s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
